import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const ToastCtx = createContext(null);

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);

    const push = useCallback((message, type = 'info', duration = 8788) => {
        // Use a combination of timestamp and random string for unique keys
        const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newToast = { id, message, type };
        setToasts(prev => [...prev, newToast]);
        if (duration > 0) {
            setTimeout(() => {
                setToasts(prev => prev.filter(t => t.id !== id));
            }, duration);
        }
        return id;
    }, []);

    const remove = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const value = { push, remove, toasts };

    const getColor = (type) => {
        switch (type) {
            case 'success': return 'bg-green-500';
            case 'error': return 'bg-red-500';
            case 'warning': return 'bg-yellow-500';
            case 'info': return 'bg-blue-500';
            default: return 'bg-gray-500';
        }
    };

    return (
        <ToastCtx.Provider value={value}>
            {children}
            <AnimatePresence mode="popLayout">
                <div className="fixed bottom-4 right-4 z-[999] space-y-2 pointer-events-none">
                    {toasts.map((toast) => (
                        <motion.div
                            key={toast.id}
                            initial={{ x: 400, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: 400, opacity: 0 }}
                            className={`${getColor(toast.type)} text-white px-4 py-3 rounded-lg shadow-lg flex items-center justify-between max-w-sm pointer-events-auto`}
                        >
                            <span className="text-sm">{toast.message}</span>
                            <button
                                onClick={() => remove(toast.id)}
                                className="ml-3 hover:opacity-80"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </motion.div>
                    ))}
                </div>
            </AnimatePresence>
        </ToastCtx.Provider>
    );
}

export const useToasts = () => {
    const context = useContext(ToastCtx);
    if (context === undefined) {
        throw new Error('useToasts must be used within a ToastProvider');
    }
    return context;
};
